import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium transition-all duration-short3 ease-standard disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50',
  {
    variants: {
      variant: {
        primary: 'bg-brand-500 text-white shadow-m1 hover:bg-brand-600 active:scale-95',
        secondary: 'border border-outline text-brand-500 hover:bg-brand-50',
        ghost: 'text-neutral-700 hover:bg-surface-variant',
        danger: 'bg-danger text-white hover:bg-danger/90',
        agent: 'text-white shadow-m1',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4',
        lg: 'h-12 px-6 text-base',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  agentColor?: 'writer' | 'director' | 'ops' | 'brain' | 'radar';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, agentColor, style, ...props }, ref) => {
    const agentStyle: Record<string, string> = {
      writer: '#6750A4',
      director: '#0061A4',
      ops: '#7D5260',
      brain: '#006E1C',
      radar: '#D97706',
    };
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        style={
          variant === 'agent' && agentColor
            ? { ...style, backgroundColor: agentStyle[agentColor] }
            : style
        }
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';
